import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import InovxLoader from '@shared/global/InovxLoader';
import { loginRequest } from './msalConfig';
import LocalPasswordChangePage from './LocalPasswordChangePage';
import {
    AuthSessionOptions,
    establishLocalPlatformSession,
    getAuthSessionOptions,
    PlatformSessionLocalAuthError,
} from './platformSession';
import '@styles/login.less';

interface LoginPageProps {
    onAuthenticated?: () => void;
}

interface FieldErrors {
    username?: string;
    password?: string;
}

function getLocalLoginFailureMessage(error: PlatformSessionLocalAuthError): string {
    switch (error.response?.Reason?.toLowerCase()) {
        case 'incorrectusernameorpassword':
            return InovxLoader.t('common:auth.invalidCredentials');
        case 'userdisabled':
            return InovxLoader.t('common:auth.localAccountDisabled');
        case 'userlogoncountnearthreshold':
            return InovxLoader.t('common:auth.localAccountBlocked');
        case 'expiredpassword':
            return InovxLoader.t('common:auth.passwordExpired');
        default:
            return error.response?.Message || InovxLoader.t('common:auth.localSignInFailed');
    }
}

const LoginPage: React.FC<LoginPageProps> = ({ onAuthenticated }) => {
    const { instance } = useMsal();
    const [options, setOptions] = React.useState<AuthSessionOptions | null>(null);
    const [optionsError, setOptionsError] = React.useState('');
    const [showVSuiteLogin, setShowVSuiteLogin] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [expiredPasswordUsername, setExpiredPasswordUsername] = useState('');

    React.useEffect(() => {
        let cancelled = false;

        async function loadOptions() {
            try {
                const loadedOptions = await getAuthSessionOptions();
                if (!cancelled) {
                    setOptions(loadedOptions);
                    setOptionsError('');
                    setShowVSuiteLogin(loadedOptions.EnableLocalLogin && !loadedOptions.EnableExternalLogin);
                }
            } catch (error) {
                if (!cancelled) {
                    setOptions(null);
                    setOptionsError(InovxLoader.t('common:auth.optionsLoadFailed'));
                }
            }
        }

        void loadOptions();

        return () => {
            cancelled = true;
        };
    }, []);

    const localLoginEnabled = options?.EnableLocalLogin === true;
    const externalLoginEnabled = options?.EnableExternalLogin === true;

    const handleAzureLogin = () => {
        instance.loginRedirect(loginRequest).catch((error) => {
            console.error('Login redirect failed:', error);
        });
    };

    const handleShowVSuiteLogin = () => {
        setShowVSuiteLogin(true);
        setErrorMessage('');
        setStatusMessage('');
        setFieldErrors({});
    };

    const handleLocalSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        setStatusMessage('');
        setFieldErrors({});

        const nextFieldErrors: FieldErrors = {};
        if (!username.trim()) {
            nextFieldErrors.username = InovxLoader.t('common:message.requiredInput');
        }
        if (!password) {
            nextFieldErrors.password = InovxLoader.t('common:message.requiredInput');
        }

        if (Object.keys(nextFieldErrors).length > 0) {
            setFieldErrors(nextFieldErrors);
            return;
        }

        setSubmitting(true);
        try {
            await establishLocalPlatformSession({ username, password });
            onAuthenticated?.();
        } catch (error) {
            if (error instanceof PlatformSessionLocalAuthError) {
                if (error.response?.Result === 'PasswordExpired') {
                    setExpiredPasswordUsername(username.trim());
                    setPassword('');
                    return;
                }

                const message = getLocalLoginFailureMessage(error);
                setErrorMessage(message);
            } else if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(InovxLoader.t('common:auth.localSignInFailed'));
            }
            setPassword('');
        } finally {
            setSubmitting(false);
        }
    };

    if (expiredPasswordUsername) {
        return (
            <LocalPasswordChangePage
                username={expiredPasswordUsername}
                onPasswordChanged={() => {
                    setExpiredPasswordUsername('');
                    setShowVSuiteLogin(true);
                    setPassword('');
                    setErrorMessage('');
                    setFieldErrors({});
                    setStatusMessage(InovxLoader.t('common:auth.passwordChangedSignIn'));
                }}
                onCancel={() => {
                    setExpiredPasswordUsername('');
                    setPassword('');
                    setErrorMessage('');
                    setStatusMessage('');
                    setFieldErrors({});
                }}
            />
        );
    }

    return (
        <div className="login-page-container">
            <div className="login-inner">
                <div className="login-panel panel">
                    <div className="login-header">
                        <div className="vlogo">
                            <img alt="Visionaize" src="/images/visionaize-login-logo.png" />
                        </div>
                        <div className="welcome-text">{InovxLoader.t('common:auth.signIn')}</div>
                    </div>
                    <div className="login-actions">
                        {optionsError && (
                            <div className="text-danger registration-error" role="alert">
                                {optionsError}
                            </div>
                        )}

                        {/* Azure SSO button */}
                        {externalLoginEnabled && (
                            <button
                                className="btn btn-info center-block login-button"
                                id="external-login-btn"
                                onClick={handleAzureLogin}
                                disabled={!options}
                            >
                                <img src="/images/corporate-logo.png" alt="" />
                                {InovxLoader.t('common:auth.corporateAccount')}
                            </button>
                        )}

                        {/* V-Suite toggle button — shown until clicked */}
                        {localLoginEnabled && !showVSuiteLogin && (
                            <button
                                className="btn btn-info center-block login-button vsuite-login-btn"
                                id="use-vsuite-btn"
                                onClick={handleShowVSuiteLogin}
                            >
                                <img src="/images/visionaize-mark.png" alt="" />
                                {InovxLoader.t('common:auth.vPlantAccount')}
                            </button>
                        )}

                        {/* V-Suite username / password form */}
                        {localLoginEnabled && showVSuiteLogin && (
                            <form id="vsuite-login-group" onSubmit={handleLocalSubmit}>
                                {errorMessage && (
                                    <div className="text-danger registration-error" role="alert">
                                        {errorMessage}
                                    </div>
                                )}
                                {statusMessage && (
                                    <div className="text-success registration-error" role="status">
                                        {statusMessage}
                                    </div>
                                )}
                                <div className={`input-group has-info${fieldErrors.username ? ' has-error' : ''}`}>
                                    <div className="input-group-addon">
                                        <span className="glyphicon glyphicon-user" title={InovxLoader.t('common:auth.username')}></span>
                                    </div>
                                    <input
                                        type="text"
                                        name="username"
                                        className="form-control"
                                        placeholder={InovxLoader.t('common:auth.username')}
                                        title={InovxLoader.t('common:auth.username')}
                                        autoComplete="username"
                                        value={username}
                                        onChange={(event) => setUsername(event.target.value)}
                                    />
                                </div>
                                {fieldErrors.username && <div className="text-danger login-field-error">{fieldErrors.username}</div>}
                                <div className={`input-group has-info${fieldErrors.password ? ' has-error' : ''}`}>
                                    <div className="input-group-addon">
                                        <span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.password')}></span>
                                    </div>
                                    <input
                                        id="Password"
                                        name="password"
                                        className="form-control"
                                        type="password"
                                        placeholder={InovxLoader.t('common:auth.password')}
                                        title={InovxLoader.t('common:auth.password')}
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}
                                    />
                                </div>
                                {fieldErrors.password && <div className="text-danger login-field-error">{fieldErrors.password}</div>}
                                <button
                                    id="signInButton"
                                    type="submit"
                                    className="btn btn-info btn-width-full login-button vsuite-login-btn"
                                    disabled={submitting}
                                >
                                    <img src="/images/visionaize-mark.png" alt="" />
                                    {submitting ? InovxLoader.t('common:auth.signingIn') : InovxLoader.t('common:auth.vPlantAccount')}
                                </button>
                            </form>
                        )}

                    </div>
                </div>
            </div>
            <p className="inovx-copyright">{InovxLoader.t('common:about.copyright')}</p>
        </div>
    );
};

export default LoginPage;
